import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Sparkles, Loader2, Copy, Check, RefreshCw, X,
  Megaphone, Home, Mail, FileText,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DRAFT_META = {
  social: { label: 'Social Post', Icon: Megaphone, hint: '≤280 chars · warm thank-you tone', maxLen: 320 },
  homepage: { label: 'Homepage Spotlight', Icon: Home, hint: '2 sentences · plain prose', maxLen: 280 },
  newsletter: { label: 'Newsletter Blurb', Icon: Mail, hint: '3-5 sentences · gratitude-first', maxLen: 700 },
  admin_note: { label: 'Admin Internal Note', Icon: FileText, hint: 'Terse changelog · ≤140 chars', maxLen: 200 },
};

const STATUS_CLS = {
  draft: 'bg-blue-100 text-blue-800 border-blue-200',
  edited: 'bg-amber-100 text-amber-800 border-amber-200',
  published: 'bg-green-100 text-green-800 border-green-200',
  discarded: 'bg-gray-100 text-gray-700 border-gray-200',
};

const fmtFull = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return ''; }
};

const DraftCard = ({ draft, onSave, onStatusChange, onCopy, copiedId }) => {
  const [content, setContent] = useState(draft.content || '');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const meta = DRAFT_META[draft.draft_type] || { label: draft.draft_type, Icon: FileText, hint: '', maxLen: 4000 };
  const Ico = meta.Icon;
  const statusCls = STATUS_CLS[draft.status] || STATUS_CLS.draft;

  useEffect(() => {
    setContent(draft.content || '');
    setDirty(false);
  }, [draft.id, draft.content]);

  const onChange = (e) => {
    setContent(e.target.value);
    setDirty(e.target.value.trim() !== (draft.content || '').trim());
  };

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await onSave(draft.id, { content });
      setDirty(false);
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-[#E5E7EB] rounded-lg bg-white" data-testid={`spotlight-draft-${draft.draft_type}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E7EB] bg-[#FAFBFC]">
        <div className="flex items-center gap-2">
          <Ico className="w-3.5 h-3.5 text-[#BA1826]" />
          <span className="text-xs font-bold text-[#0A0A0A]">{meta.label}</span>
          <span className={`text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${statusCls}`}
            data-testid={`spotlight-status-${draft.draft_type}`}>
            {draft.status}
          </span>
        </div>
        <span className="text-[10px] text-[#9CA3AF]">{meta.hint}</span>
      </div>
      <div className="p-3">
        <textarea
          value={content}
          onChange={onChange}
          maxLength={meta.maxLen}
          rows={draft.draft_type === 'newsletter' ? 5 : draft.draft_type === 'admin_note' ? 2 : 3}
          className="w-full px-3 py-2 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none resize-y leading-relaxed"
          data-testid={`spotlight-textarea-${draft.draft_type}`}
        />
        <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
          <span className="text-[10px] text-[#9CA3AF]">{content.length} / {meta.maxLen} chars</span>
          <div className="flex items-center gap-1.5">
            <button onClick={() => onCopy(draft.id, content)}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA]"
              data-testid={`spotlight-copy-${draft.draft_type}`}>
              {copiedId === draft.id ? <><Check className="w-3 h-3 text-green-600" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
            </button>
            <button onClick={save} disabled={!dirty || saving}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 bg-[#0A0A0A] text-white rounded hover:bg-[#333] disabled:opacity-40"
              data-testid={`spotlight-save-${draft.draft_type}`}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Save
            </button>
            <button onClick={() => onStatusChange(draft.id, 'published')}
              disabled={draft.status === 'published' || dirty}
              title={dirty ? 'Save your changes first' : ''}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-40"
              data-testid={`spotlight-publish-${draft.draft_type}`}>
              <Check className="w-3 h-3" /> Mark Published
            </button>
            <button onClick={() => onStatusChange(draft.id, 'discarded')}
              disabled={draft.status === 'discarded'}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 border border-[#E5E7EB] rounded text-[#4B5563] hover:bg-[#F8F9FA] disabled:opacity-40"
              data-testid={`spotlight-discard-${draft.draft_type}`}>
              <X className="w-3 h-3" /> Discard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const IdeaSpotlightPanel = ({ suggestionId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [data, setData] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState('');

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/spotlight/${suggestionId}`,
        { withCredentials: true });
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load drafts');
    } finally { setLoading(false); }
  }, [suggestionId]);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const generate = async (force = false) => {
    setGenerating(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/spotlight/generate/${suggestionId}?force=${force}`, {},
        { withCredentials: true });
      await fetchDrafts();
    } catch (e) {
      setError(e.response?.data?.detail || 'Generation failed — check that EMERGENT_LLM_KEY is configured and try again.');
    } finally { setGenerating(false); }
  };

  const saveDraft = async (draftId, body) => {
    await axios.put(`${BACKEND_URL}/api/admin/spotlight/draft/${draftId}`, body,
      { withCredentials: true });
    await fetchDrafts();
  };

  const changeStatus = async (draftId, status) => {
    await axios.put(`${BACKEND_URL}/api/admin/spotlight/draft/${draftId}`, { status },
      { withCredentials: true });
    await fetchDrafts();
  };

  const copyToClipboard = async (draftId, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(draftId);
      setTimeout(() => setCopiedId(null), 1800);
    } catch { /* ignore clipboard errors */ }
  };

  const drafts = data?.drafts || [];
  const hasDrafts = drafts.length > 0;
  const hasProtected = drafts.some(d => d.status === 'edited' || d.status === 'published');

  return (
    <div className="border border-[#BA1826]/30 bg-gradient-to-br from-[#FFF8F8] to-white rounded-lg p-4 mt-3"
      data-testid="spotlight-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#BA1826]" />
          <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">Community Idea Spotlight</h4>
          <span className="text-[9px] uppercase tracking-wider font-bold text-[#9CA3AF]">Draft only — never auto-published</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => generate(false)} disabled={generating}
            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 border border-[#BA1826] text-[#BA1826] rounded hover:bg-[#BA1826] hover:text-white disabled:opacity-50"
            data-testid="spotlight-generate-btn">
            {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {hasDrafts ? 'Regenerate' : 'Generate Drafts'}
          </button>
          {hasProtected && (
            <button onClick={() => {
              if (window.confirm('Force regenerate will OVERWRITE drafts you have already edited or marked published. Continue?')) {
                generate(true);
              }
            }} disabled={generating}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 border border-[#E5E7EB] text-[#4B5563] rounded hover:bg-[#F8F9FA] disabled:opacity-50"
              data-testid="spotlight-force-regen-btn">
              Force overwrite
            </button>
          )}
          {onClose && (
            <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="spotlight-close">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-xs mb-3">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>
      ) : !hasDrafts ? (
        <div className="text-center py-6 text-xs text-[#4B5563]" data-testid="spotlight-empty">
          {generating ? (
            <span className="inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Generating drafts with Claude Sonnet 4.5…</span>
          ) : (
            <>No drafts yet. Click <b>Generate Drafts</b> to draft 4 short pieces (Social / Homepage / Newsletter / Admin) — you can edit before anything goes public.</>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {drafts.map((d) => (
            <DraftCard
              key={d.id}
              draft={d}
              onSave={saveDraft}
              onStatusChange={changeStatus}
              onCopy={copyToClipboard}
              copiedId={copiedId}
            />
          ))}
        </div>
      )}

      {data?.last_generation && (
        <p className="text-[10px] text-[#9CA3AF] mt-3 text-right">
          Last generation: <b>{data.last_generation.status}</b>
          {data.last_generation.error ? ` (${data.last_generation.error})` : ''} ·
          {' '}{fmtFull(data.last_generation.created_at)} ·
          model: {data.model}
        </p>
      )}
    </div>
  );
};

export default IdeaSpotlightPanel;
